import type {
  AlarmUpdateDto,
  AlarmCreateDto,
  AssetUpdateDto,
  AssetCreateDto,
  AssetGroupUpdateDto,
  AssetGroupCreateDto,
  BeaconUpdateDto,
  BeaconCreateDto,
  ContactListUpdateDto,
  ContactListCreateDto,
  CustomerUpdateDto,
  CustomerCreateDto,
  CustomerAlarmCenterRelationUpdateDto,
  CustomerAlarmCenterRelationCreateDto,
  CustomerUserRelationCreateDto,
  DeviceUpdateDto,
  DeviceCreateDto,
  ObservationTriggerUpdateDto,
  ObservationTriggerCreateDto,
  OverlayUpdateDto,
  OverlayCreateDto,
  StaticResponderUpdateDto,
  StaticResponderCreateDto,
  UserUpdateDto,
  UserCreateDto,
  WebhookUpdateDto,
  WebhookCreateDto,
  ZoneUpdateDto,
  ZoneCreateDto,
} from '@x-guard/xgac-node-main-api';
import { readObservationTriggerFilter } from '../utils/observationTriggerFilter';

type RefName = 'Asset' | 'AssetGroup' | 'ContactList' | 'Zone';

const toRef = (value: any, refName: RefName) => {

  if (!value) return value;
  if (typeof value === 'string') return { _id: value, _ref: refName };
  return { _id: value._id, _ref: value._ref || refName };

};

const refArrayToIds = (values?: any[]) => (values || []).map((value) => value?._id || value);

const idsToRefArray = (values: any[] | undefined, refName: RefName) => (values || []).map((value) => toRef(value, refName));

const normalizeObservationTriggerActionForForm = (action: any) => {

  if (action?.type === 'sms' || action?.type === 'email') {

    return {
      ...action,
      config: {
        ...action.config,
        toRaw: action.config?.toRaw || [],
        toContactList: refArrayToIds(action.config?.toContactList),
        toTriggerUser: Boolean(action.config?.toTriggerUser),
      },
    };

  }
  return action;

};

const normalizeObservationTriggerActionForApi = (action: any) => {

  if (action?.type === 'alarm') {

    return {
      type: action.type,
      config: {
        severity: action.config?.severity,
        type: action.config?.type,
        procedure: action.config?.procedure || null,
        name: action.config?.name,
      },
    };

  }
  if (action?.type === 'sms') {

    return {
      type: action.type,
      config: {
        toRaw: action.config?.toRaw || [],
        toContactList: idsToRefArray(action.config?.toContactList, 'ContactList'),
        toTriggerUser: Boolean(action.config?.toTriggerUser),
        message: action.config?.message,
      },
    };

  }
  if (action?.type === 'email') {

    return {
      type: action.type,
      config: {
        toRaw: action.config?.toRaw || [],
        toContactList: idsToRefArray(action.config?.toContactList, 'ContactList'),
        toTriggerUser: Boolean(action.config?.toTriggerUser),
        message: action.config?.message,
        subject: action.config?.subject,
        ...(action.config?.locale ? { locale: action.config.locale } : {}),
      },
    };

  }
  if (action?.type === 'assetUpdate') {

    return {
      type: action.type,
      config: {
        ...(
          action.config?.available !== undefined && action.config?.available !== null && action.config.available !== ''
            ? { available: action.config.available }
            : {}
        ),
        ...(action.config?.app?.mode ? { app: { mode: action.config.app.mode } } : {}),
      },
    };

  }
  return action;

};

const normalizeObservationTriggerFilterForForm = (filter: any): any => {

  if (!filter) return filter;
  if (filter.type === 'operator/and' || filter.type === 'operator/or') {

    return {
      ...filter,
      operands: (filter.operands || []).map(normalizeObservationTriggerFilterForForm),
    };

  }
  if (filter.type === 'operator/not') {

    return {
      ...filter,
      operand: normalizeObservationTriggerFilterForForm(filter.operand),
    };

  }
  if (filter.type === 'constraint/asset') {

    return {
      ...filter,
      assets: refArrayToIds(filter.assets),
    };

  }
  if (filter.type === 'constraint/assetGroup') {

    return {
      ...filter,
      groups: refArrayToIds(filter.groups),
    };

  }
  if (filter.type === 'constraint/zone') {

    return {
      ...filter,
      zone: filter.zone?._id || filter.zone,
    };

  }
  return filter;

};

const normalizeObservationTriggerFilterForApi = (filter: any): any => {

  if (!filter) return filter;
  if (filter.type === 'operator/and' || filter.type === 'operator/or') {

    return {
      type: filter.type,
      operands: (filter.operands || []).map(normalizeObservationTriggerFilterForApi),
    };

  }
  if (filter.type === 'operator/not') {

    return {
      type: filter.type,
      operand: normalizeObservationTriggerFilterForApi(filter.operand),
    };

  }
  if (filter.type === 'constraint/asset') {

    return {
      type: filter.type,
      assets: idsToRefArray(filter.assets, 'Asset'),
    };

  }
  if (filter.type === 'constraint/assetGroup') {

    return {
      type: filter.type,
      groups: idsToRefArray(filter.groups, 'AssetGroup'),
    };

  }
  if (filter.type === 'constraint/zone') {

    return {
      type: filter.type,
      zone: toRef(filter.zone, 'Zone'),
    };

  }
  if (filter.type === 'constraint/schedule') {

    return {
      type: filter.type,
      schedule: filter.schedule,
    };

  }
  return filter;

};

export const normalizeObservationTriggerForForm = (trigger: any) => {

  const filter = normalizeObservationTriggerFilterForForm(trigger.filter);
  return {
    ...trigger,
    actions: trigger.actions?.map(normalizeObservationTriggerActionForForm) || [],
    filter,
    ...readObservationTriggerFilter(filter),
  };

};

const normalizeObservationTriggerForApi = (trigger: any) => {

  const {
    filterConditions,
    filterConditionBlockType,
    filterSchedules,
    ...payload
  } = trigger;
  delete payload.filterHasUnsupportedConditions;

  return {
    ...payload,
    ...(typeof trigger.status === 'boolean' ? { status: trigger.status ? 'active' : 'inactive' } : {}),
    ...(trigger.actions ? { actions: trigger.actions.map(normalizeObservationTriggerActionForApi) } : {}),
    ...(trigger.filter ? { filter: normalizeObservationTriggerFilterForApi(trigger.filter) } : {}),
  };

};

export const toXgacUpdateDto = (patchData: any, recordName: string) => {

  const returnData = { ...patchData };
  switch (recordName) {

  case 'alarms':
    return returnData as AlarmUpdateDto;
  case 'assets':
    return returnData as AssetUpdateDto;
  case 'asset-groups':
    return returnData as AssetGroupUpdateDto;
  case 'beacons':
    return returnData as BeaconUpdateDto;
  case 'contact-lists':
    return returnData as ContactListUpdateDto;
  case 'customers':
    return returnData as CustomerUpdateDto;
  case 'customer-alarm-center-relations':
    return returnData as CustomerAlarmCenterRelationUpdateDto;
  case 'devices':
    return returnData as DeviceUpdateDto;
  case 'observation-triggers':
    return normalizeObservationTriggerForApi(returnData) as ObservationTriggerUpdateDto;
  case 'overlays':
    return returnData as OverlayUpdateDto;
  case 'staticResponders':
    return returnData as StaticResponderUpdateDto;
  case 'users':
    return returnData as UserUpdateDto;
  case 'webhooks':
    return returnData as WebhookUpdateDto;
  case 'zones':
    return returnData as ZoneUpdateDto;
  default:
    return returnData;

  }

};

export const toXgacCreateDto = (data: any, recordName: string) => {

  const returnData = { ...data };
  switch (recordName) {

  case 'alarms':
    return returnData as AlarmCreateDto;
  case 'assets':
    return returnData as AssetCreateDto;
  case 'asset-groups':
    return returnData as AssetGroupCreateDto;
  case 'beacons':
    return returnData as BeaconCreateDto;
  case 'contact-lists':
    return returnData as ContactListCreateDto;
  case 'customers':
    return returnData as CustomerCreateDto;
  case 'customer-alarm-center-relations':
    return returnData as CustomerAlarmCenterRelationCreateDto;
  case 'customer-user-relations':
    return {
      customer: { _id: returnData.customer._id, _ref: 'Customer' },
      user: { _id: returnData['user._id'], _ref: 'User' },
      assetGroupRestrictions: returnData.assetGroupRestrictions?.map((assetGroup: string) => ({ _id: assetGroup, _ref: 'AssetGroup' })),
      roles: returnData.roles,
    } as CustomerUserRelationCreateDto;
  case 'devices':
    return returnData as DeviceCreateDto;
  case 'observation-triggers':
    return normalizeObservationTriggerForApi(returnData) as ObservationTriggerCreateDto;
  case 'overlays':
    return returnData as OverlayCreateDto;
  case 'staticResponders':
    return returnData as StaticResponderCreateDto;
  case 'users':
    return returnData as UserCreateDto;
  case 'webhooks':
    return returnData as WebhookCreateDto;
  case 'zones':
    return returnData as ZoneCreateDto;
  default:
    return returnData;

  }

};
